import { LEGACY_FEEDBACK_QUEUE } from '@pisano/feedback-core/app/containers/Flow/constants';
import { processFeedbackQueue } from '@pisano/feedback-core/app/utils/feedbackQueue/processor';
import localStorage from '@pisano/feedback-core/app/utils/localStorage';
import { createQueueCaptchaProvider } from './feedbackQueueCaptcha';
import { reportQueueFailure } from './feedbackQueueDiagnostics';
import { queueResultMessage, queueStatusMessage } from './feedbackQueueEvents';

const runtimes = new Map();

const EMPTY_SUMMARY = { pendingCount: 0, blockedCount: 0, inProgress: false };

function deliver(emit, message) {
  try {
    emit(message);
  } catch {
    // A host transport failure never changes queue processing.
  }
}

function diagnose(result) {
  try {
    reportQueueFailure(result);
  } catch {
    // Diagnostics are best effort and never change queue processing.
  }
}

function removeLegacyQueue() {
  try {
    localStorage.removeItem(LEGACY_FEEDBACK_QUEUE);
  } catch {
    // Unavailable storage has nothing to clean up and must not block the queue.
  }
}

function createRuntime(apiUrl, getCaptchaToken) {
  removeLegacyQueue();

  getCaptchaToken = getCaptchaToken || createQueueCaptchaProvider({ apiUrl });

  // Queue status is a value that new consumers receive; outcomes are events.
  const emitters = new Set();
  let summary = EMPTY_SUMMARY;

  const onSummary = (next) => {
    if (JSON.stringify(next) === JSON.stringify(summary)) return;

    summary = next;
    emitters.forEach((emit) => deliver(emit, queueStatusMessage(summary)));
  };

  const onResult = (result) => {
    diagnose(result);

    const message = queueResultMessage(result);
    if (message) emitters.forEach((emit) => deliver(emit, message));
  };

  let controller = new AbortController();
  let running = false;
  let requested = false;
  let paused = false;
  let stopped = false;

  const wake = async () => {
    if (stopped || paused || navigator.onLine === false) return;

    if (running) {
      requested = true;

      return;
    }

    running = true;
    requested = false;

    try {
      await processFeedbackQueue({ apiUrl, getCaptchaToken, signal: controller.signal, onResult, onSummary });
    } catch {
      // Storage may be denied or temporarily unavailable. The next lifecycle
      // trigger can try again; never disturb the active survey or hot-loop.
    } finally {
      running = false;
      // An aborted or failed pass is no longer sending, even without its final summary.
      if (summary.inProgress) onSummary({ ...summary, inProgress: false });

      // A trigger during the pass runs one more pass; nothing is scheduled by time.
      if (requested) void wake();
    }
  };

  const visible = () => {
    if (document.visibilityState === 'visible') void wake();
  };
  const pagehide = () => {
    paused = true;
    controller.abort();
  };
  const pageshow = () => {
    if (controller.signal.aborted) controller = new AbortController();
    paused = false;
    void wake();
  };

  window.addEventListener('online', wake);
  window.addEventListener('pagehide', pagehide);
  window.addEventListener('pageshow', pageshow);
  document.addEventListener('visibilitychange', visible);
  void wake();

  const subscribe = (emit) => {
    emitters.add(emit);
    if (JSON.stringify(summary) !== JSON.stringify(EMPTY_SUMMARY)) deliver(emit, queueStatusMessage(summary));

    return () => emitters.delete(emit);
  };

  const stop = () => {
    stopped = true;
    controller.abort();
    window.removeEventListener('online', wake);
    window.removeEventListener('pagehide', pagehide);
    window.removeEventListener('pageshow', pageshow);
    document.removeEventListener('visibilitychange', visible);
  };

  return { subscribe, stop };
}

function acquireRuntime(apiUrl, getCaptchaToken, emit) {
  let runtime = runtimes.get(apiUrl);

  if (!runtime) {
    runtime = { consumers: 0, ...createRuntime(apiUrl, getCaptchaToken) };
    runtimes.set(apiUrl, runtime);
  }

  runtime.consumers += 1;
  const unsubscribe = emit ? runtime.subscribe(emit) : undefined;

  return () => {
    runtime.consumers -= 1;
    unsubscribe?.();

    if (!runtime.consumers) {
      runtime.stop();
      runtimes.delete(apiUrl);
    }
  };
}

/**
 * Host-owned lifetime, independent of Flow mounts, resets and Redux state.
 * Each host entry enables it explicitly and may pass `emit` for background queue
 * messages. Background captcha preparation never uses the active Flow.
 */
export function startFeedbackQueueRuntime({ apiUrl, history, getCaptchaToken, emit, enabled = false } = {}) {
  if (!enabled) return () => {};

  let release;
  let stopped = false;

  const update = (location) => {
    const preview = /^\/(web_)?flow_preview\/?$/i.test(location.pathname);

    if (preview) {
      release?.();
      release = undefined;
    } else if (!release) {
      release = acquireRuntime(apiUrl, getCaptchaToken, emit);
    }
  };

  update(history?.location || window.location);
  const unlisten = history?.listen(update);

  return () => {
    if (stopped) return;

    stopped = true;
    unlisten?.();
    release?.();
    release = undefined;
  };
}
